import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { isAuthenticated } from "@/lib/auth";
import { logout } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const ADMIN_ROUTES = ["/admin/leads", "/admin/pipeline"];

export const Nav = () => {
  const { pathname } = useLocation();
  const isAdmin = ADMIN_ROUTES.some(r => pathname.startsWith(r));
  const [mobileOpen, setMobileOpen] = useState(false);

  const headerStyle = isAdmin
    ? { backgroundColor: "rgba(255,255,255,0.95)", borderBottom: "1px solid #e5e7eb", backdropFilter: "blur(12px)" }
    : { backgroundColor: "rgba(10,10,10,0.85)", borderBottom: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)" };

  const subText = isAdmin ? { color: "#9ca3af" } : { color: "rgba(255,255,255,0.35)" };
  const linkBase = "text-sm tracking-wide transition-colors duration-200 hover:text-[#FF3B30]";
  const linkColor = isAdmin ? { color: "#374151" } : { color: "rgba(255,255,255,0.75)" };
  const activeColor = { color: "#FF3B30" };

  const navLink = (to, label, testId, onClick) => {
    const isActive = to === "/" ? pathname === "/" : pathname.startsWith(to);
    return (
      <Link
        to={to}
        data-testid={testId}
        className={linkBase}
        style={isActive ? activeColor : linkColor}
        onClick={onClick}
      >
        {label}
      </Link>
    );
  };

  const handleLogout = () => {
    logout();
  };

  const mobileSheetBg = isAdmin ? "bg-white" : "bg-[#0a0a0a]";
  const mobileBorder = isAdmin ? "border-gray-200" : "border-white/10";
  const mobileIconColor = isAdmin ? "#374151" : "rgba(255,255,255,0.75)";

  return (
    <header className="sticky top-0 z-50" style={headerStyle}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={isAuthenticated() ? "/admin/leads" : "/"} data-testid={HOME.navHome} className="flex items-center gap-2">
          <img src={isAdmin ? "/logo-light-bg.png" : "/logo.png"} alt="The Managerly" className="h-8 w-auto" />
          <span className="font-mono text-[10px] hidden sm:inline" style={subText}>
            {isAdmin ? "ADMIN" : "LDN · est. 2026"}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {isAdmin ? (
            <>
              {navLink("/admin/leads", "Leads")}
              {navLink("/admin/pipeline", "Pipeline")}
              <button
                onClick={handleLogout}
                className="text-sm tracking-wide transition-colors duration-200"
                style={linkColor}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              {navLink("/", "Work", HOME.navWork)}
              <a href="#pitch" data-testid={HOME.navContact} className="text-sm tracking-wide bg-[#FF3B30] text-white px-4 py-2 hover:bg-[#E6352B] transition-colors">
                Get a redesign →
              </a>
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <div className="md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button aria-label="Open menu" className="p-2" style={{ color: mobileIconColor }}>
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className={`${mobileSheetBg} border-l ${mobileBorder} pt-16 px-8`}>
              <nav className="flex flex-col gap-6">
                {isAdmin ? (
                  <>
                    {navLink("/admin/leads", "Leads", undefined, () => setMobileOpen(false))}
                    {navLink("/admin/pipeline", "Pipeline", undefined, () => setMobileOpen(false))}
                    <button
                      onClick={() => { setMobileOpen(false); handleLogout(); }}
                      className="text-sm tracking-wide transition-colors duration-200 text-left hover:text-[#FF3B30]"
                      style={linkColor}
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <>
                    {navLink("/", "Work", undefined, () => setMobileOpen(false))}
                    <a
                      href="#pitch"
                      data-testid={undefined}
                      className="text-sm tracking-wide bg-[#FF3B30] text-white px-4 py-2 hover:bg-[#E6352B] transition-colors text-center"
                      onClick={() => setMobileOpen(false)}
                    >
                      Get a redesign →
                    </a>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Nav;
